import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/admin/api";
import { StatusBadge, Progress, money, Field } from "@/admin/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Copy, ChevronUp, ChevronDown, Clipboard, Upload, Link2, Download, Check,
} from "lucide-react";

const PHASE_STATUS = ["Not Started", "In Progress", "Waiting for Customer", "Internal Review", "Customer Review", "Blocked", "Completed", "Cancelled"];
const PROJ_STATUS = ["Lead", "Confirmed", "Not Started", "In Progress", "Waiting for Customer", "On Hold", "Completed", "Cancelled"];
const COMM = ["WhatsApp", "Email", "Phone Call", "Meeting", "Internal Discussion", "Customer Feedback", "Other"];
const METHODS = ["Bank Transfer", "Mobile Money", "Cash", "Card", "Other"];
const ACTION_STATUS = ["Requested", "Reminder Sent", "Received", "No Longer Required", "Overdue"];
const CR_STATUS = ["Under Review", "Approved", "Declined", "Implemented"];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [notes, setNotes] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [actions, setActions] = useState([]);
  const [crs, setCrs] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [files, setFiles] = useState([]);
  const [activity, setActivity] = useState([]);

  const loadProject = useCallback(async () => {
    const { data } = await api.get(`/admin/projects/${id}`);
    setP(data);
  }, [id]);

  const loadAll = useCallback(async () => {
    try {
      const [pr, n, d, a, c, m, f, act] = await Promise.all([
        api.get(`/admin/projects/${id}`), api.get(`/admin/projects/${id}/notes`),
        api.get(`/admin/projects/${id}/decisions`), api.get(`/admin/projects/${id}/customer-actions`),
        api.get(`/admin/projects/${id}/change-requests`), api.get(`/admin/projects/${id}/payment-milestones`),
        api.get(`/admin/projects/${id}/files`), api.get(`/admin/projects/${id}/activity`),
      ]);
      setP(pr.data); setNotes(n.data); setDecisions(d.data); setActions(a.data);
      setCrs(c.data); setMilestones(m.data); setFiles(f.data); setActivity(act.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (!p) return <div className="text-mutedink">Loading project…</div>;
  const pay = p.payment;

  const setStatus = async (status) => {
    try { await api.put(`/admin/projects/${id}`, { status }); loadAll(); toast.success("Status updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/admin")} className="flex items-center gap-1 text-sm text-mutedink hover:text-navy"><ArrowLeft className="h-4 w-4" /> Projects</button>

      <div className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-navy/10 bg-white p-5">
        <div>
          <div className="text-xs uppercase tracking-wide text-mutedink">{p.customer_name} · {p.type}</div>
          <h1 className="font-serif text-3xl font-700 text-navy">{p.name}</h1>
          <div className="mt-1 text-sm text-mutedink">Owner: {p.owner || "—"} · Start {p.start_date || "—"} · Launch {p.expected_launch || "—"}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-48"><Progress value={p.progress} /></div>
          <Select value={p.status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-52 bg-cream" data-testid="project-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PROJ_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex w-full flex-wrap justify-start gap-1 bg-transparent p-0">
          {["overview", "timeline", "phases", "notes", "decisions", "actions", "changes", "payments", "invoices", "files", "activity"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
              className="rounded-full border border-navy/15 bg-white px-4 py-1.5 text-sm capitalize data-[state=active]:bg-navy data-[state=active]:text-white">
              {t === "actions" ? "Customer Actions" : t === "changes" ? "Change Requests" : t === "notes" ? "Notes & Comms" : t}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-5">
          <Overview p={p} pay={pay} actions={actions} id={id} />
        </TabsContent>

        {/* TIMELINE */}
        <TabsContent value="timeline" className="mt-5">
          <Timeline phases={p.phases} />
        </TabsContent>

        {/* PHASES */}
        <TabsContent value="phases" className="mt-5">
          <Phases p={p} reload={loadAll} />
        </TabsContent>

        {/* NOTES */}
        <TabsContent value="notes" className="mt-5">
          <Notes id={id} phases={p.phases} notes={notes} reload={loadAll} />
        </TabsContent>

        {/* DECISIONS */}
        <TabsContent value="decisions" className="mt-5">
          <Decisions id={id} phases={p.phases} decisions={decisions} reload={loadAll} />
        </TabsContent>

        {/* CUSTOMER ACTIONS */}
        <TabsContent value="actions" className="mt-5">
          <Actions id={id} phases={p.phases} actions={actions} reload={loadAll} />
        </TabsContent>

        {/* CHANGE REQUESTS */}
        <TabsContent value="changes" className="mt-5">
          <Changes id={id} crs={crs} reload={loadAll} />
        </TabsContent>

        {/* PAYMENTS */}
        <TabsContent value="payments" className="mt-5">
          <PaymentsTab id={id} p={p} pay={pay} milestones={milestones} reload={loadAll} />
        </TabsContent>

        {/* INVOICES */}
        <TabsContent value="invoices" className="mt-5">
          <Invoices id={id} />
        </TabsContent>

        {/* FILES */}
        <TabsContent value="files" className="mt-5">
          <Files id={id} files={files} reload={loadAll} />
        </TabsContent>

        {/* ACTIVITY */}
        <TabsContent value="activity" className="mt-5">
          <Activity activity={activity} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Card = ({ title, children, action }) => (
  <div className="rounded-xl border border-navy/10 bg-white p-5">
    {(title || action) && <div className="mb-4 flex items-center justify-between"><h3 className="font-serif text-lg font-600 text-navy">{title}</h3>{action}</div>}
    {children}
  </div>
);

function Overview({ p, pay, actions, id }) {
  const [cu, setCu] = useState(null);
  const open = actions.filter((a) => ["Requested", "Reminder Sent", "Overdue"].includes(a.status));
  const genUpdate = async () => {
    try { const { data } = await api.get(`/admin/projects/${id}/customer-update`); setCu(data.text); }
    catch (e) { toast.error(apiError(e)); }
  };
  const copy = () => { navigator.clipboard.writeText(cu); toast.success("Copied to clipboard"); };
  const facts = [
    ["Status", p.status], ["Type", p.type], ["Owner", p.owner || "—"], ["Currency", p.currency],
    ["Start date", p.start_date || "—"], ["Expected launch", p.expected_launch || "—"],
    ["Current phase", p.current_phase || "—"],
    ["Next deadline", p.next_deadline ? `${p.next_deadline} (${p.days_until_deadline}d)` : "—"],
  ];
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2 space-y-4">
        <Card title="Project overview">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
            {facts.map(([k, v]) => (<div key={k}><div className="text-xs text-mutedink">{k}</div><div className="text-sm font-600 text-navy">{v}</div></div>))}
          </div>
          <div className="mt-4"><Progress value={p.progress} /></div>
          {p.description && <p className="mt-4 text-sm text-ink">{p.description}</p>}
        </Card>
        <Card title="Customer update" action={<button onClick={genUpdate} data-testid="gen-update-btn" className="rounded-full bg-navy px-4 py-1.5 text-sm text-white">Generate</button>}>
          {cu ? (
            <div>
              <Textarea rows={12} value={cu} onChange={(e) => setCu(e.target.value)} className="font-mono text-xs" data-testid="customer-update-text" />
              <button onClick={copy} className="mt-3 inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-600 text-navy-dark"><Clipboard className="h-4 w-4" /> Copy to clipboard</button>
            </div>
          ) : <p className="text-sm text-mutedink">Generate a clean summary (only shareable notes/decisions are included) to send via WhatsApp or email.</p>}
        </Card>
      </div>
      <div className="space-y-4">
        <Card title="Payment">
          <div className="space-y-2 text-sm">
            <Row k="Contract value" v={money(pay.currency, pay.contract_value)} />
            <Row k="Invoiced" v={money(pay.currency, pay.total_invoiced)} />
            <Row k="Received" v={money(pay.currency, pay.total_received)} green />
            <Row k="Outstanding" v={money(pay.currency, pay.total_outstanding)} bold />
            <Progress value={pay.percent_paid} />
            {pay.next_due && <div className="mt-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">Next due: {money(pay.currency, pay.next_due.amount)}{pay.next_due.due_date ? ` by ${pay.next_due.due_date}` : ""}</div>}
          </div>
        </Card>
        <Card title={`Open customer actions (${open.length})`}>
          {open.length ? <ul className="space-y-2">{open.map((a) => (
            <li key={a.id} className="rounded-lg bg-sky-50 px-3 py-2 text-sm text-sky-900">{a.description}{a.due_date ? <span className="text-xs text-sky-700"> · by {a.due_date}</span> : ""}</li>
          ))}</ul> : <p className="text-sm text-mutedink">Nothing outstanding from the customer.</p>}
        </Card>
      </div>
    </div>
  );
}
const Row = ({ k, v, green, bold }) => (<div className="flex justify-between"><span className="text-mutedink">{k}</span><span className={`${green ? "text-green-600" : "text-navy"} ${bold ? "font-700" : "font-600"}`}>{v}</span></div>);

function Timeline({ phases }) {
  const dates = phases.flatMap((p) => [p.start_date, p.end_date]).filter(Boolean).map((d) => new Date(d));
  if (!dates.length) return <Card><p className="text-sm text-mutedink">Add a start date to generate a timeline.</p></Card>;
  const min = new Date(Math.min(...dates)), max = new Date(Math.max(...dates));
  const span = Math.max(1, (max - min) / 86400000);
  const color = (s) => s === "Completed" ? "bg-green-500" : s === "Blocked" ? "bg-red-500" : s.includes("Customer") || s.includes("Waiting") ? "bg-sky-500" : s === "In Progress" ? "bg-gold" : "bg-navy/30";
  return (
    <Card title="Timeline">
      <div className="space-y-2">
        {phases.map((ph) => {
          const s = ph.start_date ? new Date(ph.start_date) : min;
          const e = ph.end_date ? new Date(ph.end_date) : s;
          const left = ((s - min) / 86400000 / span) * 100;
          const width = Math.max(3, (((e - s) / 86400000 + 1) / span) * 100);
          return (
            <div key={ph.id} className="grid grid-cols-12 items-center gap-2">
              <div className="col-span-4 truncate text-sm text-navy md:col-span-3">{ph.name}</div>
              <div className="col-span-8 md:col-span-9">
                <div className="relative h-6 rounded bg-cream">
                  <div className={`absolute top-0 h-6 rounded ${color(ph.status)}`} style={{ left: `${left}%`, width: `${width}%` }} title={`${ph.start_date} → ${ph.end_date}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex justify-between text-xs text-mutedink"><span>{min.toISOString().slice(0, 10)}</span><span>{max.toISOString().slice(0, 10)}</span></div>
    </Card>
  );
}

function Phases({ p, reload }) {
  const [open, setOpen] = useState(false);
  const move = async (idx, dir) => {
    const arr = [...p.phases];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    try { await api.post(`/admin/projects/${p.id}/phases/reorder`, { order: arr.map((x) => x.id) }); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} data-testid="add-phase-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-600 text-navy-dark"><Plus className="h-4 w-4" /> Add phase</button></div>
      {p.phases.map((ph, idx) => <PhaseCard key={ph.id} pid={p.id} ph={ph} idx={idx} count={p.phases.length} move={move} reload={reload} />)}
      {p.phases.length === 0 && <Card><p className="text-sm text-mutedink">No phases yet.</p></Card>}
      <AddDialog open={open} onClose={() => setOpen(false)} title="Add phase" fields={[
        ["name", "Name", "text"], ["duration_days", "Duration (days)", "number"], ["start_date", "Start date", "date"], ["end_date", "End date", "date"], ["responsible", "Responsible", "text"], ["description", "Description", "textarea"],
      ]} onSave={async (v) => { await api.post(`/admin/projects/${p.id}/phases`, v); reload(); }} />
    </div>
  );
}

function PhaseCard({ pid, ph, idx, count, move, reload }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(ph);
  const [shift, setShift] = useState(true);
  const [taskDesc, setTaskDesc] = useState("");
  useEffect(() => setF(ph), [ph]);
  const save = async () => {
    try {
      await api.put(`/admin/projects/${pid}/phases/${ph.id}`, {
        name: f.name, description: f.description, start_date: f.start_date, end_date: f.end_date,
        duration_days: Number(f.duration_days), status: f.status, progress: Number(f.progress || 0),
        responsible: f.responsible, customer_action_required: f.customer_action_required, shift_subsequent: shift,
      });
      setEdit(false); reload(); toast.success("Phase updated");
    } catch (e) { toast.error(apiError(e)); }
  };
  const quickStatus = async (status) => { try { await api.put(`/admin/projects/${pid}/phases/${ph.id}`, { status }); reload(); } catch (e) { toast.error(apiError(e)); } };
  const addTask = async () => { if (!taskDesc) return; await api.post(`/admin/projects/${pid}/phases/${ph.id}/tasks`, { description: taskDesc, is_customer: true }); setTaskDesc(""); reload(); };
  const toggleTask = async (t) => { await api.put(`/admin/projects/${pid}/phases/${ph.id}/tasks/${t.id}`, { status: t.status === "Completed" ? "Not Started" : "Completed" }); reload(); };
  const remove = async () => { await api.delete(`/admin/projects/${pid}/phases/${ph.id}`); reload(); };
  const dup = async () => { await api.post(`/admin/projects/${pid}/phases/${ph.id}/duplicate`); reload(); };

  return (
    <div className="rounded-xl border border-navy/10 bg-white p-4" data-testid={`phase-${ph.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col"><button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-mutedink disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button><button onClick={() => move(idx, 1)} disabled={idx === count - 1} className="text-mutedink disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button></div>
        <div className="flex-1">
          <div className="font-600 text-navy">{idx + 1}. {ph.name}</div>
          <div className="text-xs text-mutedink">{ph.start_date || "—"} → {ph.end_date || "—"} · {ph.duration_days}d {ph.responsible ? `· ${ph.responsible}` : ""} {ph.customer_action_required ? "· customer" : ""}</div>
        </div>
        <div className="w-32"><Progress value={ph.progress} /></div>
        <Select value={ph.status} onValueChange={quickStatus}>
          <SelectTrigger className="h-8 w-44 bg-cream text-xs" data-testid={`phase-status-${ph.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{PHASE_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-1">
          <button onClick={() => setEdit(!edit)} className="rounded-lg border border-navy/15 px-2 py-1 text-xs text-navy">Edit</button>
          <button onClick={dup} className="rounded-lg border border-navy/15 px-2 py-1 text-navy" title="Duplicate"><Copy className="h-3.5 w-3.5" /></button>
          <button onClick={remove} className="rounded-lg border border-red-200 px-2 py-1 text-red-500" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      {edit && (
        <div className="mt-4 grid grid-cols-1 gap-3 border-t border-navy/10 pt-4 md:grid-cols-3">
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Duration (days)"><Input type="number" value={f.duration_days} onChange={(e) => setF({ ...f, duration_days: e.target.value })} /></Field>
          <Field label="Responsible"><Input value={f.responsible || ""} onChange={(e) => setF({ ...f, responsible: e.target.value })} /></Field>
          <Field label="Start date"><Input type="date" value={f.start_date || ""} onChange={(e) => setF({ ...f, start_date: e.target.value })} /></Field>
          <Field label="End date"><Input type="date" value={f.end_date || ""} onChange={(e) => setF({ ...f, end_date: e.target.value })} /></Field>
          <Field label="Progress %"><Input type="number" value={f.progress || 0} onChange={(e) => setF({ ...f, progress: e.target.value })} /></Field>
          <div className="md:col-span-3"><Field label="Description"><Textarea rows={2} value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field></div>
          <label className="flex items-center gap-2 text-sm text-navy"><Checkbox checked={f.customer_action_required} onCheckedChange={(v) => setF({ ...f, customer_action_required: !!v })} /> Customer action required</label>
          <label className="flex items-center gap-2 text-sm text-navy"><Checkbox checked={shift} onCheckedChange={(v) => setShift(!!v)} /> Shift subsequent phases when end date changes</label>
          <div><button onClick={save} className="rounded-full bg-gold px-5 py-2 text-sm font-600 text-navy-dark">Save phase</button></div>
        </div>
      )}

      {ph.tasks?.length > 0 || edit ? (
        <div className="mt-3 border-t border-navy/10 pt-3">
          <div className="mb-2 text-xs font-600 uppercase tracking-wide text-mutedink">Checklist</div>
          <ul className="space-y-1">
            {ph.tasks?.map((t) => (
              <li key={t.id} className="flex items-center gap-2 text-sm">
                <button onClick={() => toggleTask(t)} className={`flex h-4 w-4 items-center justify-center rounded border ${t.status === "Completed" ? "bg-green-500 border-green-500 text-white" : "border-navy/30"}`}>{t.status === "Completed" && <Check className="h-3 w-3" />}</button>
                <span className={t.status === "Completed" ? "text-mutedink line-through" : "text-ink"}>{t.description}</span>
                <span className="text-[0.65rem] text-mutedink">{t.is_customer ? "customer" : "internal"}</span>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex gap-2"><Input value={taskDesc} onChange={(e) => setTaskDesc(e.target.value)} placeholder="Add checklist item…" className="h-8 text-sm" /><button onClick={addTask} className="rounded-lg bg-navy px-3 text-sm text-white">Add</button></div>
        </div>
      ) : null}
    </div>
  );
}

/* Generic add dialog */
function AddDialog({ open, onClose, title, fields, onSave, initial = {} }) {
  const [v, setV] = useState(initial);
  useEffect(() => { if (open) setV(initial); /* eslint-disable-next-line */ }, [open]);
  const save = async () => { try { await onSave(v); toast.success("Saved"); onClose(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-xl text-navy">{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {fields.map(([key, label, type, options]) => (
            <Field key={key} label={label}>
              {type === "textarea" ? <Textarea rows={3} value={v[key] || ""} onChange={(e) => setV({ ...v, [key]: e.target.value })} />
                : type === "select" ? (
                  (() => {
                    const opts = options.map((o) => (typeof o === "object" ? o : { value: o, label: o }));
                    const current = v[key] ?? opts[0]?.value;
                    return (
                      <Select value={current} onValueChange={(val) => setV({ ...v, [key]: val })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    );
                  })()
                ) : <Input type={type} value={v[key] || ""} onChange={(e) => setV({ ...v, [key]: e.target.value })} />}
            </Field>
          ))}
        </div>
        <DialogFooter><button onClick={save} data-testid="dialog-save" className="rounded-full bg-gold px-6 py-2 font-600 text-navy-dark">Save</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Notes({ id, phases, notes, reload }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} data-testid="add-note-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-600 text-navy-dark"><Plus className="h-4 w-4" /> Add note</button></div>
      {notes.map((n) => (
        <div key={n.id} className="rounded-xl border border-navy/10 bg-white p-4" data-testid={`note-${n.id}`}>
          <div className="flex flex-wrap items-center gap-2 text-xs text-mutedink">
            <span className="rounded bg-navy/10 px-2 py-0.5 text-navy">{n.comm_type}</span>
            <span>{new Date(n.created_at).toLocaleString()}</span>
            <span>· {n.created_by}</span>
            <span className={`ml-auto rounded px-2 py-0.5 ${n.visibility === "shareable" ? "bg-green-100 text-green-700" : "bg-slate/15 text-slate"}`}>{n.visibility === "shareable" ? "Can share" : "Internal only"}</span>
          </div>
          <div className="mt-2 font-600 text-navy">{n.subject}</div>
          {n.details && <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{n.details}</p>}
          <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-xs text-mutedink md:grid-cols-2">
            {n.customer_request && <div><b>Customer asked:</b> {n.customer_request}</div>}
            {n.sankofa_recommendation && <div><b>We recommended:</b> {n.sankofa_recommendation}</div>}
            {n.agreed && <div><b>Agreed:</b> {n.agreed}</div>}
            {n.info_missing && <div><b>Still missing:</b> {n.info_missing}</div>}
            {n.next_action && <div><b>Next action:</b> {n.next_action} {n.follow_up_date ? `(${n.follow_up_date})` : ""}</div>}
          </div>
        </div>
      ))}
      {notes.length === 0 && <Card><p className="text-sm text-mutedink">No notes yet. Every note is kept in full history.</p></Card>}
      <AddDialog open={open} onClose={() => setOpen(false)} title="Add note" onSave={async (v) => { const p = { ...v, phase_id: v.phase_id === "__none__" ? null : v.phase_id }; await api.post(`/admin/projects/${id}/notes`, p); reload(); }}
        fields={[
          ["comm_type", "Communication type", "select", COMM], ["phase_id", "Phase", "select", [{ value: "__none__", label: "No phase" }, ...phases.map((p) => ({ value: p.id, label: p.name }))]],
          ["subject", "Subject", "text"], ["details", "Detailed notes", "textarea"],
          ["customer_request", "What did the customer request?", "textarea"], ["sankofa_recommendation", "What did Sankofa recommend?", "textarea"],
          ["agreed", "What was agreed?", "textarea"], ["info_missing", "Information still missing", "textarea"],
          ["next_action", "Next action", "text"], ["responsible", "Responsible", "text"], ["follow_up_date", "Follow-up date", "date"],
          ["links", "Attachments / links", "text"], ["visibility", "Visibility", "select", ["internal", "shareable"]],
        ]} />
    </div>
  );
}

function Decisions({ id, phases, decisions, reload }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} data-testid="add-decision-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-600 text-navy-dark"><Plus className="h-4 w-4" /> Add decision</button></div>
      {decisions.map((d) => (
        <div key={d.id} className="rounded-xl border border-navy/10 bg-white p-4" data-testid={`decision-${d.id}`}>
          <div className="flex items-center gap-2 text-xs text-mutedink"><span>{d.decision_date}</span>{d.visibility === "shareable" && <span className="rounded bg-green-100 px-2 py-0.5 text-green-700">Can share</span>}</div>
          <div className="mt-1 font-600 text-navy">{d.decision}</div>
          <div className="mt-1 grid grid-cols-2 gap-x-6 text-xs text-mutedink md:grid-cols-4">
            {d.requested_by && <div><b>By:</b> {d.requested_by}</div>}
            {d.approved_by && <div><b>Approved:</b> {d.approved_by}</div>}
            {d.impact_cost && <div><b>Cost:</b> {d.impact_cost}</div>}
            {d.impact_timeline && <div><b>Timeline:</b> {d.impact_timeline}</div>}
          </div>
        </div>
      ))}
      {decisions.length === 0 && <Card><p className="text-sm text-mutedink">No decisions logged.</p></Card>}
      <AddDialog open={open} onClose={() => setOpen(false)} title="Log decision" onSave={async (v) => { await api.post(`/admin/projects/${id}/decisions`, v); reload(); }}
        fields={[["decision_date", "Decision date", "date"], ["decision", "Decision", "textarea"], ["requested_by", "Requested by", "text"], ["approved_by", "Approved by", "text"], ["impact_scope", "Impact on scope", "text"], ["impact_timeline", "Impact on timeline", "text"], ["impact_cost", "Impact on cost", "text"], ["notes", "Notes", "textarea"], ["visibility", "Visibility", "select", ["internal", "shareable"]]]} />
    </div>
  );
}

function Actions({ id, phases, actions, reload }) {
  const [open, setOpen] = useState(false);
  const upd = async (a, status) => { try { await api.put(`/admin/projects/${id}/customer-actions/${a.id}`, { status, description: a.description }); reload(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} data-testid="add-action-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-600 text-navy-dark"><Plus className="h-4 w-4" /> Request item</button></div>
      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase text-mutedink"><tr><th className="px-4 py-2">Item</th><th className="px-4 py-2">Requested</th><th className="px-4 py-2">Due</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Set</th></tr></thead>
          <tbody className="divide-y divide-navy/5">
            {actions.map((a) => (
              <tr key={a.id} data-testid={`action-${a.id}`}>
                <td className="px-4 py-2 text-navy">{a.description}</td>
                <td className="px-4 py-2 text-mutedink">{a.date_requested}</td>
                <td className="px-4 py-2 text-mutedink">{a.due_date || "—"}</td>
                <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                <td className="px-4 py-2">
                  <Select value={a.status} onValueChange={(v) => upd(a, v)}><SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{ACTION_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
                </td>
              </tr>
            ))}
            {actions.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-mutedink">Nothing requested yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <AddDialog open={open} onClose={() => setOpen(false)} title="Request customer item" onSave={async (v) => { const p = { ...v, phase_id: v.phase_id === "__none__" ? null : v.phase_id }; await api.post(`/admin/projects/${id}/customer-actions`, p); reload(); }}
        fields={[["description", "Description (e.g. Logo, Photos)", "text"], ["date_requested", "Date requested", "date"], ["due_date", "Due date", "date"], ["responsible_person", "Customer responsible", "text"], ["phase_id", "Phase", "select", [{ value: "__none__", label: "No phase" }, ...phases.map((p) => ({ value: p.id, label: p.name }))]]]} />
    </div>
  );
}

function Changes({ id, crs, reload }) {
  const [open, setOpen] = useState(false);
  const setStatus = async (cr, status) => { try { await api.put(`/admin/projects/${id}/change-requests/${cr.id}`, { status }); reload(); toast.success("Updated"); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} data-testid="add-cr-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-600 text-navy-dark"><Plus className="h-4 w-4" /> New change request</button></div>
      {crs.map((c) => (
        <div key={c.id} className="rounded-xl border border-navy/10 bg-white p-4" data-testid={`cr-${c.id}`}>
          <div className="flex items-center justify-between">
            <div className="font-600 text-navy">{c.customer_request}</div>
            <StatusBadge status={c.status} />
          </div>
          <div className="mt-1 text-xs text-mutedink">{c.date} · +{c.additional_days || 0} days · +{Number(c.additional_cost || 0).toLocaleString()} {c.requested_change ? `· ${c.requested_change}` : ""}</div>
          {c.status !== "Approved" && c.status !== "Implemented" && (
            <div className="mt-3 flex gap-2">
              <button onClick={() => setStatus(c, "Approved")} data-testid={`cr-approve-${c.id}`} className="rounded-full bg-green-600 px-4 py-1.5 text-xs font-600 text-white">Approve &amp; add to contract</button>
              <button onClick={() => setStatus(c, "Declined")} className="rounded-full border border-red-200 px-4 py-1.5 text-xs text-red-600">Decline</button>
            </div>
          )}
        </div>
      ))}
      {crs.length === 0 && <Card><p className="text-sm text-mutedink">No change requests.</p></Card>}
      <AddDialog open={open} onClose={() => setOpen(false)} title="Change request" onSave={async (v) => { await api.post(`/admin/projects/${id}/change-requests`, { ...v, additional_cost: parseFloat(v.additional_cost || 0), additional_days: parseInt(v.additional_days || 0) }); reload(); }}
        fields={[["date", "Date", "date"], ["customer_request", "Customer request", "textarea"], ["original_scope", "Original scope", "textarea"], ["requested_change", "Requested change", "textarea"], ["additional_work", "Additional work", "textarea"], ["additional_days", "Additional days", "number"], ["additional_cost", "Additional cost", "number"]]} />
    </div>
  );
}

function PaymentsTab({ id, p, pay, milestones, reload }) {
  const [addOpen, setAddOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card><div className="text-xs text-mutedink">Contract</div><div className="font-serif text-xl font-700 text-navy">{money(pay.currency, pay.contract_value)}</div></Card>
        <Card><div className="text-xs text-mutedink">Invoiced</div><div className="font-serif text-xl font-700 text-navy">{money(pay.currency, pay.total_invoiced)}</div></Card>
        <Card><div className="text-xs text-mutedink">Received</div><div className="font-serif text-xl font-700 text-green-600">{money(pay.currency, pay.total_received)}</div></Card>
        <Card><div className="text-xs text-mutedink">Outstanding</div><div className="font-serif text-xl font-700 text-amber-600">{money(pay.currency, pay.total_outstanding)}</div></Card>
      </div>
      <div className="flex justify-end"><button onClick={() => setAddOpen(true)} data-testid="add-milestone-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-600 text-navy-dark"><Plus className="h-4 w-4" /> Add milestone</button></div>
      {milestones.map((m) => (
        <div key={m.id} className="rounded-xl border border-navy/10 bg-white p-4" data-testid={`milestone-${m.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1"><div className="font-600 text-navy">{m.description || "Milestone"}</div><div className="text-xs text-mutedink">{m.invoice_number ? `Inv ${m.invoice_number} · ` : ""}Due {m.due_date || "—"}</div></div>
            <div className="text-sm text-navy">Due {money(m.currency, m.amount_due)}</div>
            <div className="text-sm text-green-600">Paid {money(m.currency, m.amount_paid)}</div>
            <div className="text-sm font-700 text-amber-600">Out {money(m.currency, m.outstanding)}</div>
            <StatusBadge status={m.status} />
            <button onClick={() => setPayFor(m)} data-testid={`record-payment-${m.id}`} className="rounded-full bg-navy px-3 py-1.5 text-xs text-white">Record payment</button>
          </div>
          {m.payments?.length > 0 && (
            <div className="mt-2 border-t border-navy/10 pt-2 text-xs text-mutedink">
              {m.payments.map((pm) => <div key={pm.id}>• {money(m.currency, pm.amount)} on {pm.payment_date} ({pm.method})</div>)}
            </div>
          )}
        </div>
      ))}
      {milestones.length === 0 && <Card><p className="text-sm text-mutedink">No payment milestones yet.</p></Card>}

      <AddDialog open={addOpen} onClose={() => setAddOpen(false)} title="Add payment milestone" onSave={async (v) => { await api.post(`/admin/projects/${id}/payment-milestones`, { ...v, amount_due: parseFloat(v.amount_due || 0) }); reload(); }}
        fields={[["description", "Description", "text"], ["invoice_number", "Invoice / reference", "text"], ["amount_due", "Amount due", "number"], ["due_date", "Due date", "date"]]} />

      {payFor && (
        <AddDialog open={!!payFor} onClose={() => setPayFor(null)} title={`Record payment — ${payFor.description}`} onSave={async (v) => { await api.post(`/admin/projects/${id}/payment-milestones/${payFor.id}/payments`, { ...v, amount: parseFloat(v.amount || 0) }); reload(); }}
          fields={[["amount", "Amount", "number"], ["payment_date", "Payment date", "date"], ["method", "Method", "select", METHODS], ["notes", "Notes", "text"]]} />
      )}
    </div>
  );
}

function Files({ id, files, reload }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "Document");
    try { await api.post(`/admin/projects/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } }); reload(); toast.success("Uploaded"); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };
  const download = async (f) => {
    if (f.link_url) { window.open(f.link_url, "_blank"); return; }
    try {
      const r = await api.get(`/admin/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = f.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (f) => { await api.delete(`/admin/files/${f.id}`); reload(); };
  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-600 text-navy-dark" data-testid="upload-file-btn">
          <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload file"}
          <input type="file" className="hidden" onChange={upload} disabled={uploading} />
        </label>
        <button onClick={() => setLinkOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-navy/15 bg-white px-4 py-2 text-sm text-navy"><Link2 className="h-4 w-4" /> Add link</button>
      </div>
      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase text-mutedink"><tr><th className="px-4 py-2">Name</th><th className="px-4 py-2">Kind</th><th className="px-4 py-2">Added</th><th className="px-4 py-2 text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-navy/5">
            {files.map((f) => (
              <tr key={f.id} data-testid={`file-${f.id}`}>
                <td className="px-4 py-2 text-navy">{f.original_filename}</td>
                <td className="px-4 py-2 text-mutedink">{f.kind}{f.link_url ? " (link)" : ""}</td>
                <td className="px-4 py-2 text-mutedink">{new Date(f.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => download(f)} className="mr-2 text-navy"><Download className="inline h-4 w-4" /></button>
                  <button onClick={() => del(f)} className="text-red-500"><Trash2 className="inline h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {files.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-mutedink">No files or links yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <AddDialog open={linkOpen} onClose={() => setLinkOpen(false)} title="Add link" onSave={async (v) => { await api.post(`/admin/projects/${id}/files/link`, v); reload(); }}
        fields={[["name", "Name", "text"], ["link_url", "URL", "text"], ["kind", "Kind", "select", ["Link", "Google Drive", "Domain info", "Design files", "Other"]]]} />
    </div>
  );
}

function Invoices({ id }) {
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get(`/admin/projects/${id}/invoices`).then((r) => setInvoices(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  const download = async (inv) => {
    try {
      const r = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `Invoice-${inv.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (inv) => { await api.delete(`/admin/invoices/${inv.id}`); load(); };

  return (
    <div className="space-y-3">
      <div className="flex justify-end"><button onClick={() => setOpen(true)} data-testid="add-invoice-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-600 text-navy-dark"><Plus className="h-4 w-4" /> New invoice</button></div>
      <div className="overflow-hidden rounded-xl border border-navy/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase text-mutedink"><tr><th className="px-4 py-2">Number</th><th className="px-4 py-2">Date</th><th className="px-4 py-2">Due</th><th className="px-4 py-2 text-right">Subtotal</th><th className="px-4 py-2 text-right">Paid</th><th className="px-4 py-2 text-right">Balance</th><th className="px-4 py-2 text-right">Actions</th></tr></thead>
          <tbody className="divide-y divide-navy/5">
            {invoices.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-${inv.id}`}>
                <td className="px-4 py-2 font-600 text-navy">{inv.number}</td>
                <td className="px-4 py-2 text-mutedink">{inv.date}</td>
                <td className="px-4 py-2 text-mutedink">{inv.due_date || "—"}</td>
                <td className="px-4 py-2 text-right">{money(inv.currency, inv.subtotal)}</td>
                <td className="px-4 py-2 text-right text-green-600">{money(inv.currency, inv.amount_paid)}</td>
                <td className="px-4 py-2 text-right font-700 text-navy">{money(inv.currency, inv.balance)}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => download(inv)} data-testid={`invoice-pdf-${inv.id}`} className="mr-2 inline-flex items-center gap-1 rounded-full bg-navy px-3 py-1 text-xs text-white"><Download className="h-3.5 w-3.5" /> PDF</button>
                  <button onClick={() => del(inv)} className="text-red-500"><Trash2 className="inline h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {invoices.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-mutedink">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <InvoiceDialog id={id} open={open} onClose={() => setOpen(false)} reload={load} />
    </div>
  );
}

function InvoiceDialog({ id, open, onClose, reload }) {
  const [ccy, setCcy] = useState("GHS");
  const [dueDate, setDueDate] = useState("");
  const [paid, setPaid] = useState(0);
  const [notes, setNotes] = useState("20/30/30/20 milestone terms apply.");
  const [items, setItems] = useState([]);
  const [hostingSug, setHostingSug] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get(`/admin/projects/${id}/invoice-defaults`).then((r) => {
      const d = r.data;
      setCcy(d.currency); setPaid(d.amount_paid);
      setItems([{ ...d.package_line }]);
      setHostingSug(d.suggested_hosting);
    }).catch((e) => toast.error(apiError(e)));
  }, [open, id]);

  const setItem = (i, key, val) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const addRow = (row) => setItems((arr) => [...arr, row || { description: "", quantity: 1, unit_price: 0 }]);
  const removeRow = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const subtotal = items.reduce((a, it) => a + (parseFloat(it.quantity || 0) * parseFloat(it.unit_price || 0)), 0);
  const balance = subtotal - parseFloat(paid || 0);

  const save = async () => {
    const clean = items.filter((it) => it.description).map((it) => ({ description: it.description, quantity: parseFloat(it.quantity || 1), unit_price: parseFloat(it.unit_price || 0) }));
    if (!clean.length) { toast.error("Add at least one line item"); return; }
    try {
      await api.post(`/admin/projects/${id}/invoices`, { currency: ccy, due_date: dueDate || null, amount_paid: parseFloat(paid || 0), notes, line_items: clean });
      toast.success("Invoice created"); setDueDate(""); setNotes("20/30/30/20 milestone terms apply."); onClose(); reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">New invoice</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Field label="Currency"><Input value={ccy} onChange={(e) => setCcy(e.target.value)} /></Field>
          <Field label="Due date"><Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="inv-due" /></Field>
          <Field label="Payments received"><Input type="number" value={paid} onChange={(e) => setPaid(e.target.value)} data-testid="inv-paid" /></Field>
        </div>
        <div className="mt-2">
          <div className="mb-1 text-xs font-600 text-navy">Line items</div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-2">
                <Input className="col-span-6" placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`inv-desc-${i}`} />
                <Input className="col-span-2" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
                <Input className="col-span-3" type="number" placeholder="Unit price" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`inv-price-${i}`} />
                <button onClick={() => removeRow(i)} className="col-span-1 text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <button onClick={() => addRow()} className="rounded-full border border-navy/15 px-3 py-1.5 text-xs text-navy">+ Add line</button>
            {hostingSug && <button onClick={() => addRow({ ...hostingSug })} className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1.5 text-xs text-navy">+ Hosting advance (12 mo)</button>}
          </div>
        </div>
        <div className="mt-3 ml-auto w-64 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-mutedink">Subtotal</span><span className="font-600 text-navy">{money(ccy, subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-mutedink">Payments received</span><span className="text-green-600">- {money(ccy, paid)}</span></div>
          <div className="flex justify-between border-t border-navy/10 pt-1"><span className="font-700 text-navy">Balance due</span><span className="font-700 text-navy">{money(ccy, balance)}</span></div>
        </div>
        <Field label="Notes"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        <DialogFooter>
          <button onClick={onClose} className="rounded-full px-5 py-2 text-navy">Cancel</button>
          <button onClick={save} data-testid="inv-create" className="rounded-full bg-gold px-6 py-2 font-600 text-navy-dark">Create invoice</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Activity({ activity }) {
  return (
    <Card title="Activity history">
      <ol className="space-y-2">
        {activity.map((a) => (
          <li key={a.id} className="flex items-start gap-3 text-sm">
            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-gold" />
            <div>
              <span className="text-navy">{a.action}</span>
              {a.new_value ? <span className="text-mutedink"> — {String(a.new_value)}</span> : null}
              <div className="text-xs text-mutedink">{new Date(a.created_at).toLocaleString()} · {a.user}</div>
            </div>
          </li>
        ))}
        {activity.length === 0 && <p className="text-sm text-mutedink">No activity recorded yet.</p>}
      </ol>
    </Card>
  );
}
